/**
 * Claim Organization Page
 * Allows users to find and claim organizations without administrators
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Building2,
  Search,
  Loader2,
  GraduationCap,
  Crown,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Org type colors
const orgTypeConfig = {
  fraternity: { label: 'Fraternity', color: 'bg-blue-500/10 text-blue-400' },
  sorority: { label: 'Sorority', color: 'bg-pink-500/10 text-pink-400' },
  club: { label: 'Club', color: 'bg-amber-500/10 text-amber-400' },
  promoter: { label: 'Promoter', color: 'bg-purple-500/10 text-purple-400' },
  organization: { label: 'Organization', color: 'bg-gray-500/10 text-gray-400' },
};

export const ClaimOrganization = () => {
  const { session, user } = useAuth();
  const navigate = useNavigate();
  
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [claiming, setClaiming] = useState(null);
  const [claimSuccess, setClaimSuccess] = useState(null);

  // Fetch claimable organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);

      const res = await fetch(`${API_URL}/api/organizations/claimable?${params}`);

      if (res.ok) {
        const data = await res.json();
        setOrganizations(data.organizations || []);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Claim organization
  const handleClaim = async (orgId) => {
    if (!session?.access_token) {
      toast.error('Please sign in to claim an organization');
      navigate('/login');
      return;
    }

    setClaiming(orgId);
    try {
      const res = await fetch(`${API_URL}/api/organizations/${orgId}/claim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to claim organization');
      }

      setClaimSuccess(data.organization);
      toast.success(data.message);
      
      // Redirect to org dashboard after a moment
      setTimeout(() => {
        navigate('/org/command-center');
      }, 2000);

    } catch (error) {
      toast.error(error.message);
    } finally {
      setClaiming(null);
    }
  };

  // Success state
  if (claimSuccess) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-cool-mint/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-cool-mint" />
          </div>
          <h1 className="text-2xl font-bold text-cloud-white mb-2">
            You're now the admin of {claimSuccess.name}!
          </h1>
          <p className="text-soft-silver mb-6">
            Redirecting you to your organization's dashboard...
          </p>
          <Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <div className="border-b border-white/[0.06] bg-graphite/50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
              <Crown className="w-7 h-7 text-electric-ice-blue" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-cloud-white">Claim Your Organization</h1>
              <p className="text-soft-silver">Find and claim your organization to become its administrator</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-text" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for your organization..."
            className="pl-12 h-12 bg-graphite border-white/[0.08] text-cloud-white"
          />
        </div>

        {/* Info Box */}
        <div className="p-4 rounded-xl bg-electric-ice-blue/5 border border-electric-ice-blue/20 mb-8">
          <p className="text-soft-silver text-sm">
            <span className="text-electric-ice-blue font-medium">How it works:</span> Organizations without an administrator can be claimed by signing up. 
            Once you claim an organization, you become its owner and can manage events, members, and settings.
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          </div>
        )}

        {/* Organizations List */}
        {!loading && organizations.length === 0 && (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-muted-text mx-auto mb-4" />
            <h3 className="text-lg font-medium text-cloud-white mb-2">
              {searchQuery ? 'No organizations found' : 'All organizations are claimed'}
            </h3>
            <p className="text-soft-silver">
              {searchQuery 
                ? 'Try a different search term' 
                : 'Check back later or contact support to add your organization'}
            </p>
          </div>
        )}

        {!loading && organizations.length > 0 && (
          <div className="grid gap-4">
            {organizations.map((org) => {
              const typeConfig = orgTypeConfig[org.type] || orgTypeConfig.organization;
              
              return (
                <div 
                  key={org.id}
                  className="p-4 rounded-xl bg-graphite/50 border border-white/[0.06] hover:border-white/[0.12] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-cool-mint/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-7 h-7 text-cool-mint" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-cloud-white">{org.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", typeConfig.color)}>
                          {typeConfig.label}
                        </span>
                        {org.campus_name && (
                          <span className="flex items-center gap-1 text-xs text-muted-text">
                            <GraduationCap className="w-3 h-3" />
                            {org.campus_name}
                          </span>
                        )}
                      </div>
                      {org.description && (
                        <p className="text-sm text-soft-silver mt-2 line-clamp-2">{org.description}</p>
                      )}
                    </div>

                    <Button
                      onClick={() => handleClaim(org.id)}
                      disabled={claiming === org.id}
                      className="bg-electric-ice-blue text-obsidian hover:bg-electric-ice-blue/90 flex-shrink-0"
                    >
                      {claiming === org.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Claim
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Not signed in notice */}
        {!session && (
          <div className="mt-8 p-4 rounded-xl bg-soft-amber/10 border border-soft-amber/20">
            <p className="text-soft-amber text-sm">
              You need to <button onClick={() => navigate('/login')} className="underline font-medium">sign in</button> or{' '}
              <button onClick={() => navigate('/signup')} className="underline font-medium">create an account</button> to claim an organization.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimOrganization;
